import Phaser from "phaser";
import i18next from "i18next";
import { COLORS, darkenColor, hexToPhaser } from "../../../ui/colors";
import { WindowManager } from "../../../ui/window-manager";
import { ModernButton } from "../../../ui/modern-button";
import { BaseWindow } from "../../../ui/windows/base-window";
import { NotificationHandler } from "../../../ui/notification-handler";
import { PortalSpinner } from "../../../ui/portal-spinner";
import { playSound } from "../../../utils/sound-utils";
import { trackEvent } from "../../../utils/ga-events";
import { generateUUID } from "../../../utils/uuid";
import { changeLanguage, SUPPORTED_LANGUAGES, LANGUAGE_NAMES, LANGUAGE_FLAGS } from "../../../i18n/config";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";

const MUSIC_KEY = "musicEnabled";
const SFX_KEY = "sfxEnabled";

export class SettingsWindow extends BaseWindow {
  protected windowManager: WindowManager;
  protected uiLayer: Phaser.GameObjects.Container;
  private onCloseCallback: (() => void) | null = null;
  private htmlInputElement: HTMLInputElement | null = null;
  private emailInputContainer: Phaser.GameObjects.Container | null = null;
  private redeemCodeContainer: Phaser.GameObjects.Container | null = null;
  private notificationHandler: NotificationHandler;
  private portalSpinner: PortalSpinner | null = null;

  private isValidEmail(email: string): boolean {
    if (email.includes(" ")) return false;
    const atIndex = email.indexOf("@");
    if (atIndex === -1) return false;
    const afterAt = email.substring(atIndex + 1);
    if (!afterAt.includes(".")) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  constructor(
    scene: Phaser.Scene,
    windowManager: WindowManager,
    uiLayer: Phaser.GameObjects.Container,
  ) {
    super(scene);
    this.windowManager = windowManager;
    this.uiLayer = uiLayer;
    this.notificationHandler = new NotificationHandler(scene);
    this.portalSpinner = new PortalSpinner(scene);
    this.portalSpinner.hide(); // Hidden by default
  }

  protected getLayoutOverrides(config: { title: string; side: "left" | "right" | "center" }): {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  } {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const safeLeft = getSafeAreaInsetLeft();
    const leftMargin = Math.max(100, 16 + safeLeft + 48 + 16);
    const width = gameWidth - leftMargin - 48;
    // Center the window horizontally instead of anchoring it to the left margin.
    const x = (gameWidth - width) / 2;
    const y = 32;
    const height = gameHeight - y - 20;
    return { x, y, width, height };
  }

  show(onClose?: () => void) {
    this.hide();
    if (!this.scene) return;
    this.onCloseCallback = onClose ?? null;

    const baseElements = this.createBaseWindow({
      title: i18next.t("ui:settings"),
      side: "center",
    });

    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // Split into two columns
    const columnWidth = (width - 16) / 2; // 16px gap between columns
    const leftColumnX = x + this.CONTENT_PADDING_X;
    const rightColumnX = x + columnWidth + 24; // offset for right column
    
    const contentStartY = y + 76;
    let currentY = contentStartY + 16;

    const containerElements: Phaser.GameObjects.GameObject[] = [...baseElements];

    // ========== LEFT COLUMN ==========
    
    // --- Music Toggle ---
    const musicLabel = this.scene.add
      .text(leftColumnX, currentY, i18next.t("ui:labels.music"), {
        fontSize: "16px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
      })
      .setOrigin(0, 0.5);
    musicLabel.setScrollFactor(0);

    const musicToggle = this.createToggle(
      leftColumnX + columnWidth - this.CONTENT_PADDING_X - 16,
      currentY,
      localStorage.getItem(MUSIC_KEY) !== "false",
      (enabled) => {
        localStorage.setItem(MUSIC_KEY, String(enabled));
        const audioManager = (this.scene as any).audioManager;
        if (audioManager) {
          if (enabled) {
            audioManager.start();
          } else {
            audioManager.stop();
          }
        }
        const setMusicEnabled = (this.scene as any).setMusicEnabled;
        if (typeof setMusicEnabled === "function") {
          setMusicEnabled.call(this.scene, enabled);
        }
        if (this.scene.cache.audio.exists("menu-click-sound")) {
          playSound(this.scene, "menu-click-sound", null, 0.4);
        }
      },
    );

    containerElements.push(musicLabel, ...musicToggle);
    currentY += 52;

    // --- SFX Toggle ---
    const sfxLabel = this.scene.add
      .text(leftColumnX, currentY, i18next.t("ui:labels.sfx"), {
        fontSize: "16px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
      })
      .setOrigin(0, 0.5);
    sfxLabel.setScrollFactor(0);

    const sfxToggle = this.createToggle(
      leftColumnX + columnWidth - this.CONTENT_PADDING_X - 16,
      currentY,
      localStorage.getItem(SFX_KEY) !== "false",
      (enabled) => {
        localStorage.setItem(SFX_KEY, String(enabled));
        if (!enabled) {
          const audioManager = (this.scene as any).audioManager;
          audioManager?.stopAmbient();
          const baseFireSound = (this.scene as any).baseFireSound;
          if (baseFireSound) {
            baseFireSound.stop();
          }
        }
        if (this.scene.cache.audio.exists("menu-click-sound")) {
          playSound(this.scene, "menu-click-sound", null, 0.4);
        }
      },
    );

    containerElements.push(sfxLabel, ...sfxToggle);
    currentY += 64;

    // --- Redeem Code Button ---
    const redeemButton = new ModernButton({
      scene: this.scene,
      x: leftColumnX + columnWidth / 2,
      y: currentY,
      text: i18next.t("ui:buttons.redeemCode"),
      style: "success",
      onClick: () => {
        if (this.scene.cache.audio.exists("menu-click-sound")) {
          playSound(this.scene, "menu-click-sound", null, 0.4);
        }
        this.showRedeemCodeWindow();
      },
    });

    containerElements.push(redeemButton);

    // ========== RIGHT COLUMN ==========
    
    const player = (window as any).player;
    const accountMail: string = player?.data?.accountMail || "";
    const hasEmail = accountMail.length > 0;

    let rightColumnY = contentStartY + 16;
    const rightColButtonSpacing = 16;

    // --- Language Selector ---
    const languageLabel = this.scene.add
      .text(rightColumnX, rightColumnY, i18next.t("ui:labels.language") || "LANGUAGE", {
        fontSize: "14px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
      })
      .setOrigin(0, 0.5);
    languageLabel.setScrollFactor(0);

    const currentLang = i18next.language;
    const languageButton = new ModernButton({
      scene: this.scene,
      x: rightColumnX + columnWidth / 2,
      y: rightColumnY,
      text: `${LANGUAGE_FLAGS[currentLang as keyof typeof LANGUAGE_FLAGS] ?? ''} ${(LANGUAGE_NAMES[currentLang as keyof typeof LANGUAGE_NAMES] || 'English').toUpperCase()}`.trim(),
      size: "icon",
      style: "primary",
      onClick: () => {
        if (this.scene.cache.audio.exists("menu-click-sound")) {
          playSound(this.scene, "menu-click-sound", null, 0.4);
        }
        this.showLanguageSelector();
      },
    });

    containerElements.push(languageLabel, languageButton);
    rightColumnY += 68;

    if (!hasEmail) {
      // Show JOIN and LOGIN buttons
      const joinButton = new ModernButton({
        scene: this.scene,
        x: rightColumnX + (columnWidth - 32) / 2,
        y: rightColumnY,
        text: i18next.t("ui:buttons.join") || "JOIN",
        style: "success",
        onClick: () => {
          trackEvent("create-account-clicked");
          this.showEmailInputWindow("create-account");
        },
      });
      containerElements.push(joinButton);
      rightColumnY += 32 + rightColButtonSpacing;

      const loginButton = new ModernButton({
        scene: this.scene,
        x: rightColumnX + (columnWidth - 32) / 2,
        y: rightColumnY,
        text: i18next.t("ui:buttons.login") || "LOGIN",
        style: "primary",
        onClick: () => {
          trackEvent("login-clicked");
          this.showEmailInputWindow("login");
        },
      });
      containerElements.push(loginButton);
    } else {
      // Show LOGOUT button
      const logoutButton = new ModernButton({
        scene: this.scene,
        x: rightColumnX + (columnWidth - 32) / 2,
        y: rightColumnY,
        text: i18next.t("ui:buttons.logout") || "LOGOUT",
        style: "danger",
        onClick: () => {
          this.handleLogout();
        },
      });
      containerElements.push(logoutButton);
    }

    const backButton = new ModernButton({
      scene: this.scene,
      x: x + width / 2,
      y: y + height - 12,
      text: i18next.t("ui:buttons.back") || "BACK",
      style: "secondary",
      onClick: () => {
        if (this.onCloseCallback) {
          this.onCloseCallback();
        } else {
          this.hide();
        }
      },
    });
    backButton.setScrollFactor(0);
    containerElements.push(backButton);

    this.container = this.scene.add.container(0, 0, containerElements);
    this.container.setDepth(100000);
    this.container.setVisible(true);
    this.visible = true;

    this.uiLayer.add(this.container);

    // Update close button visibility after showing
    if (
      this.windowManager &&
      typeof (this.windowManager as any).updateCloseButtonVisibility ===
        "function"
    ) {
      (this.windowManager as any).updateCloseButtonVisibility();
    }
  }

  hide() {
    this.hideEmailInputWindow();
    this.hideRedeemCodeWindow();
    super.hide();
    this.onCloseCallback = null;
  }

  public showCreateAccountEmailInputWindow() {
    this.showEmailInputWindow("create-account");
  }

  private showRedeemCodeWindow() {
    this.hideRedeemCodeWindow();

    // Check if the player has an email — if not, show create-account flow instead
    const player = (window as any).player;
    const accountMail: string = player?.data?.accountMail || "";
    if (!accountMail) {
      this.showCreateAccountForRedeem();
      return;
    }

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 260;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2 + inset, panelY - panelHeight / 2 + inset, panelWidth - inset * 2, panelHeight - inset * 2, Math.max(cornerRadius - inset, 4));
    panelBg.setScrollFactor(0);

    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, i18next.t("ui:dialogs.redeemCode"), {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    // Status text (shows feedback after submission)
    const statusText = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 58, "", {
        fontSize: "13px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    statusText.setScrollFactor(0);

    // HTML input
    const inputPadding = 24;
    const inputWidth = panelWidth - inputPadding * 2;
    const inputHeight = 56;
    const inputX = panelX;
    const inputY = panelY - 10;

    this.htmlInputElement = document.createElement("input");
    this.htmlInputElement.type = "text";
    this.htmlInputElement.placeholder = i18next.t("ui:placeholders.redeemCode");
    this.htmlInputElement.style.position = "absolute";

    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;
    const screenX = canvasRect.left + inputX * scaleX;
    const screenY = canvasRect.top + inputY * scaleY;
    const screenWidth = inputWidth * scaleX;
    const screenHeight = inputHeight * scaleY;

    this.htmlInputElement.style.left = `${screenX - screenWidth / 2}px`;
    this.htmlInputElement.style.top = `${screenY - screenHeight / 2}px`;
    this.htmlInputElement.style.width = `${screenWidth}px`;
    this.htmlInputElement.style.height = `${screenHeight}px`;
    this.htmlInputElement.style.fontSize = `${22 * Math.min(scaleX, scaleY)}px`;
    this.htmlInputElement.style.fontFamily = "Anton, sans-serif";
    this.htmlInputElement.style.backgroundColor = `#${hexToPhaser(COLORS.GRAY).toString(16).padStart(6, "0")}`;
    this.htmlInputElement.style.color = COLORS.WHITE;
    this.htmlInputElement.style.border = `3px solid ${COLORS.WHITE}`;
    this.htmlInputElement.style.padding = "12px";
    this.htmlInputElement.style.textAlign = "center";
    this.htmlInputElement.style.outline = "none";
    this.htmlInputElement.style.zIndex = "100002";
    this.htmlInputElement.style.boxSizing = "border-box";
    this.htmlInputElement.style.textTransform = "uppercase";
    document.body.appendChild(this.htmlInputElement);

    const btnGap = 16;
    const btnY = panelY + panelHeight / 2 - 40;

    const cancelButton = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.cancel"),
      style: "danger",
      onClick: () => this.hideRedeemCodeWindow(),
    });
    cancelButton.setScrollFactor(0);

    const redeemButton = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.redeem"),
      style: "success",
      disabled: true,
      onClick: () => {
        const code = this.htmlInputElement?.value.trim() || "";
        if (!code) return;

        redeemButton.setDisabled(true);
        cancelButton.setDisabled(true);
        statusText.setText(i18next.t("ui:messages.redeeming"));
        statusText.setColor(COLORS.WHITE);

        (window as any).socket?.emit("redeem-code", { code }, (res: any) => {
          if (res?.success) {
            trackEvent("code-redeemed", { code: code });
            statusText.setText(i18next.t("ui:messages.codeRedeemed"));
            statusText.setColor(COLORS.GREEN);
            if (this.htmlInputElement) this.htmlInputElement.value = "";
            if (this.scene.cache.audio.exists("menu-click-sound")) {
              playSound(this.scene, "menu-click-sound", null, 0.4);
            }
            // Auto-close after 2s
            this.scene.time.delayedCall(2000, () => this.hideRedeemCodeWindow());
          } else {
            statusText.setText(res?.error || "Failed to redeem code.");
            statusText.setColor(COLORS.RED);
            redeemButton.setDisabled(false);
            cancelButton.setDisabled(false);
          }
        });
      },
    });
    redeemButton.setScrollFactor(0);

    // Position buttons
    const totalWidth = (cancelButton as any).buttonWidth + btnGap + (redeemButton as any).buttonWidth;
    cancelButton.setX(panelX - totalWidth / 2 + (cancelButton as any).buttonWidth / 2);
    redeemButton.setX(panelX + totalWidth / 2 - (redeemButton as any).buttonWidth / 2);

    this.htmlInputElement.addEventListener("input", () => {
      if (this.htmlInputElement) {
        this.htmlInputElement.value = this.htmlInputElement.value.toUpperCase();
      }
      const isValid = (this.htmlInputElement?.value.trim().length ?? 0) >= 3;
      redeemButton.setDisabled(!isValid);
    });

    this.htmlInputElement.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter" && !(redeemButton as any).isDisabled) {
        redeemButton.emit("pointerdown");
      }
    });

    setTimeout(() => this.htmlInputElement?.focus(), 100);

    this.redeemCodeContainer = this.scene.add.container(0, 0, [
      bgOverlay,
      panelBg,
      title,
      statusText,
      cancelButton,
      redeemButton,
    ]);
    this.redeemCodeContainer.setDepth(100002);
    this.uiLayer.add(this.redeemCodeContainer);
  }

  private showCreateAccountForRedeem() {
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 290;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2 + inset, panelY - panelHeight / 2 + inset, panelWidth - inset * 2, panelHeight - inset * 2, Math.max(cornerRadius - inset, 4));
    panelBg.setScrollFactor(0);

    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, i18next.t("ui:dialogs.createAccount"), {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    const subtitle = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 60, i18next.t("ui:dialogs.accountRequired"), {
        fontSize: "13px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: panelWidth - 48 },
      })
      .setOrigin(0.5, 0);
    subtitle.setScrollFactor(0);

    const inputPadding = 24;
    const inputWidth = panelWidth - inputPadding * 2;
    const inputHeight = 56;
    const inputX = panelX;
    const inputY = panelY - 10;

    this.htmlInputElement = document.createElement("input");
    this.htmlInputElement.type = "email";
    this.htmlInputElement.placeholder = i18next.t("ui:placeholders.email");
    this.htmlInputElement.style.position = "absolute";

    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;
    const screenX = canvasRect.left + inputX * scaleX;
    const screenY = canvasRect.top + inputY * scaleY;
    const screenWidth = inputWidth * scaleX;
    const screenHeight = inputHeight * scaleY;

    this.htmlInputElement.style.left = `${screenX - screenWidth / 2}px`;
    this.htmlInputElement.style.top = `${screenY - screenHeight / 2}px`;
    this.htmlInputElement.style.width = `${screenWidth}px`;
    this.htmlInputElement.style.height = `${screenHeight}px`;
    this.htmlInputElement.style.fontSize = `${22 * Math.min(scaleX, scaleY)}px`;
    this.htmlInputElement.style.fontFamily = "Anton, sans-serif";
    this.htmlInputElement.style.backgroundColor = `#${hexToPhaser(COLORS.GRAY).toString(16).padStart(6, "0")}`;
    this.htmlInputElement.style.color = COLORS.WHITE;
    this.htmlInputElement.style.border = `3px solid ${COLORS.WHITE}`;
    this.htmlInputElement.style.padding = "12px";
    this.htmlInputElement.style.textAlign = "center";
    this.htmlInputElement.style.outline = "none";
    this.htmlInputElement.style.zIndex = "100002";
    this.htmlInputElement.style.boxSizing = "border-box";
    document.body.appendChild(this.htmlInputElement);

    const statusText = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 58, "", {
        fontSize: "13px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    statusText.setScrollFactor(0);

    const btnGap = 16;
    const btnY = panelY + panelHeight / 2 - 40;

    const cancelButton = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.cancel"),
      style: "danger",
      onClick: () => this.hideRedeemCodeWindow(),
    });
    cancelButton.setScrollFactor(0);

    const createButton = new ModernButton({
      scene: this.scene,
      x: 0,
      y: btnY,
      text: i18next.t("ui:buttons.createAccount"),
      style: "success",
      disabled: true,
      onClick: () => {
        const email = this.htmlInputElement?.value.trim() || "";
        if (!email || !this.isValidEmail(email)) return;

        createButton.setDisabled(true);
        cancelButton.setDisabled(true);
        statusText.setText(i18next.t("ui:messages.redeeming"));
        statusText.setColor(COLORS.WHITE);

        const accountId = (window as any).accountId;
        const guestToken = localStorage.getItem("guestToken");
        const socket = (window as any).socket;
        if (!socket) return;

        socket.emit("create-account-email", { email, accountId, guestToken }, (response: any) => {
          if (response?.success) {
            statusText.setText(i18next.t("ui:messages.emailVerification"));
            statusText.setColor(COLORS.GREEN);
            if (this.htmlInputElement) this.htmlInputElement.value = "";
            this.scene.time.delayedCall(3000, () => this.hideRedeemCodeWindow());
          } else {
            statusText.setText(response?.error || "Failed to create account.");
            statusText.setColor(COLORS.RED);
            createButton.setDisabled(false);
            cancelButton.setDisabled(false);
          }
        });
      },
    });
    createButton.setScrollFactor(0);

    const totalWidth = (cancelButton as any).buttonWidth + btnGap + (createButton as any).buttonWidth;
    cancelButton.setX(panelX - totalWidth / 2 + (cancelButton as any).buttonWidth / 2);
    createButton.setX(panelX + totalWidth / 2 - (createButton as any).buttonWidth / 2);

    this.htmlInputElement.addEventListener("input", () => {
      const email = this.htmlInputElement?.value || "";
      createButton.setDisabled(!this.isValidEmail(email.trim()));
    });

    this.htmlInputElement.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter" && !(createButton as any).isDisabled) {
        createButton.emit("pointerdown");
      }
    });

    setTimeout(() => this.htmlInputElement?.focus(), 100);

    this.redeemCodeContainer = this.scene.add.container(0, 0, [
      bgOverlay,
      panelBg,
      title,
      subtitle,
      statusText,
      cancelButton,
      createButton,
    ]);
    this.redeemCodeContainer.setDepth(100002);
    this.uiLayer.add(this.redeemCodeContainer);
  }

  private hideRedeemCodeWindow() {
    if (this.htmlInputElement) {
      this.htmlInputElement.remove();
      this.htmlInputElement = null;
    }
    if (this.redeemCodeContainer) {
      this.redeemCodeContainer.destroy();
      this.redeemCodeContainer = null;
    }
  }

  /**
   * Creates a toggle switch and returns all its game objects.
   */
  private createToggle(
    rightX: number,
    centerY: number,
    initialValue: boolean,
    onChange: (enabled: boolean) => void,
  ): Phaser.GameObjects.GameObject[] {
    const trackW = 52;
    const trackH = 26;
    const knobSize = 20;
    const trackX = rightX - trackW;

    let enabled = initialValue;

    const track = this.scene.add.graphics();
    const knob = this.scene.add.graphics();

    const drawToggle = () => {
      const trackColor = enabled ? hexToPhaser(COLORS.GREEN) : 0x555555;
      track.clear();
      track.fillStyle(trackColor, 1);
      track.fillRoundedRect(trackX, centerY - trackH / 2, trackW, trackH, trackH / 2);
      track.lineStyle(2, enabled ? 0x117744 : 0x1a3550, 1);
      track.strokeRoundedRect(trackX, centerY - trackH / 2, trackW, trackH, trackH / 2);

      knob.clear();
      knob.fillStyle(0xffffff, 1);
      const knobX = enabled
        ? trackX + trackW - knobSize / 2 - 3
        : trackX + knobSize / 2 + 3;
      knob.fillCircle(knobX, centerY, knobSize / 2);
    };

    drawToggle();
    track.setScrollFactor(0);
    knob.setScrollFactor(0);

    // Clickable zone over the track
    const hitZone = this.scene.add
      .zone(trackX + trackW / 2, centerY, trackW, trackH)
      .setInteractive({ useHandCursor: true });
    hitZone.setScrollFactor(0);

    hitZone.on("pointerdown", () => {
      enabled = !enabled;
      drawToggle();
      onChange(enabled);
    });

    return [track, knob, hitZone];
  }

  /**
   * Show a language selector popup dialog
   */
  private showLanguageSelector() {
    if (!this.scene) return;

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    // Create background overlay
    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    // Create panel
    const panelWidth = Math.min(320, gameWidth - 32);
    const panelHeight = 380;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2 + inset, panelY - panelHeight / 2 + inset, panelWidth - inset * 2, panelHeight - inset * 2, Math.max(cornerRadius - inset, 4));
    panelBg.setScrollFactor(0);

    // Title
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, i18next.t("ui:labels.language") || "LANGUAGE", {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    const containerElements: Phaser.GameObjects.GameObject[] = [bgOverlay, panelBg, title];

    const buttonSpacing = 12;
    const buttonsPerRow = 2;
    const buttonWidth = (panelWidth - 32) / buttonsPerRow - buttonSpacing; // panel width minus padding
    const contentStartY = panelY - panelHeight / 2 + 100;

    let buttonIndex = 0;
    // Create language buttons in a 2-column grid
    SUPPORTED_LANGUAGES.forEach((lang) => {
      const langName = LANGUAGE_NAMES[lang];
      const row = Math.floor(buttonIndex / buttonsPerRow);
      const col = buttonIndex % buttonsPerRow;

      const buttonX = panelX - panelWidth / 2 + 16 + (col * (buttonWidth + buttonSpacing)) + buttonWidth / 2;
      const buttonY = contentStartY + row * 70;

      const btn = new ModernButton({
        scene: this.scene,
        x: buttonX,
        y: buttonY,
        text: `${LANGUAGE_FLAGS[lang]} ${langName}`,
        size: "icon",
        style: i18next.language === lang ? "success" : "primary",
        onClick: async () => {
          await changeLanguage(lang);
          // Refresh the settings window after language change completes
          const savedCallback = this.onCloseCallback;
          this.hideLanguageSelector(languageSelector);
          this.show(savedCallback ?? undefined);
        },
      });

      btn.setWidth(buttonWidth);
      containerElements.push(btn);
      buttonIndex++;
    });

    // Close button
    const closeButton = new ModernButton({
      scene: this.scene,
      x: panelX,
      y: panelY + panelHeight / 2 - 40,
      text: i18next.t("ui:buttons.cancel"),
      style: "danger",
      onClick: () => this.hideLanguageSelector(languageSelector),
    });
    containerElements.push(closeButton);

    // Create container
    const languageSelector = this.scene.add.container(0, 0, containerElements);
    languageSelector.setDepth(100001);
    languageSelector.setScrollFactor(0);
    this.uiLayer.add(languageSelector);

    // Store reference to close it later
    (this as any)._languageSelectorContainer = languageSelector;
  }

  /**
   * Hide the language selector popup
   */
  private hideLanguageSelector(container?: Phaser.GameObjects.Container) {
    if (container) {
      container.destroy();
    } else if ((this as any)._languageSelectorContainer) {
      (this as any)._languageSelectorContainer.destroy();
      (this as any)._languageSelectorContainer = null;
    }
  }

  private handleLogout() {
    // Clear the guest token from localStorage
    localStorage.removeItem("guestToken");

    // Show spinner immediately
    this.showLoadingSpinner();

    // Hide all windows
    this.hide();
    this.windowManager.hideAll();
    this.windowManager.hidePlayerWidget();

    // Hide action slots
    const actionSlotsContainer = (this.windowManager as any)
      .actionSlotsContainer;
    if (actionSlotsContainer) {
      actionSlotsContainer.setVisible(false);
    }

    // Show logout confirmation notification after a short delay
    this.scene.time.delayedCall(500, () => {
      this.notificationHandler.show(
        "✓ Logged out successfully. Reloading...",
        "success",
      );

      // Reload the page after the notification delay
      this.scene.time.delayedCall(1500, () => {
        window.location.reload();
      });
    });
  }

  private showLoadingSpinner() {
    // Implementation similar to ProfileWindow
  }

  private showEmailInputWindow(mode: "create-account" | "login" = "create-account") {
    this.hideEmailInputWindow();
    this.windowManager.hideAll();
    this.windowManager.hidePlayerWidget();

    // Hide action slots
    const actionSlotsContainer = (this.windowManager as any).actionSlotsContainer;
    if (actionSlotsContainer) {
      actionSlotsContainer.setVisible(false);
    }

    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;

    const bgOverlay = this.scene.add.rectangle(
      gameWidth / 2,
      gameHeight / 2,
      gameWidth,
      gameHeight,
      hexToPhaser(COLORS.BLACK),
      0.8,
    );
    bgOverlay.setOrigin(0.5);
    bgOverlay.setScrollFactor(0);

    const panelWidth = Math.min(400, gameWidth - 32);
    const panelHeight = 250;
    const panelX = gameWidth / 2;
    const panelY = gameHeight / 2;
    const cornerRadius = 12;

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(hexToPhaser(COLORS.BG), 0.92);
    panelBg.fillRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const outerBorderWidth = 4;
    panelBg.lineStyle(outerBorderWidth, hexToPhaser(COLORS.BLACK), 0.8);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2, panelY - panelHeight / 2, panelWidth, panelHeight, cornerRadius);
    const innerBorderWidth = 4;
    const inset = (outerBorderWidth + innerBorderWidth) / 2;
    const darkerBG = darkenColor(hexToPhaser(COLORS.BG), 0.3);
    panelBg.lineStyle(innerBorderWidth, darkerBG, 0.9);
    panelBg.strokeRoundedRect(panelX - panelWidth / 2 + inset, panelY - panelHeight / 2 + inset, panelWidth - inset * 2, panelHeight - inset * 2, Math.max(cornerRadius - inset, 4));
    panelBg.setScrollFactor(0);

    const titleText = mode === "create-account" ? "Create Account" : "Login";
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 24, titleText, {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
      })
      .setOrigin(0.5, 0);
    title.setScrollFactor(0);

    // Email input
    const inputPadding = 24;
    const inputWidth = panelWidth - inputPadding * 2;
    const inputHeight = 56;
    const inputX = panelX;
    const inputY = panelY - 10;

    this.htmlInputElement = document.createElement("input");
    this.htmlInputElement.type = "email";
    this.htmlInputElement.placeholder = "your@email.com";
    this.htmlInputElement.style.position = "absolute";

    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / gameWidth;
    const scaleY = canvasRect.height / gameHeight;
    const screenX = canvasRect.left + inputX * scaleX;
    const screenY = canvasRect.top + inputY * scaleY;
    const screenWidth = inputWidth * scaleX;
    const screenHeight = inputHeight * scaleY;

    this.htmlInputElement.style.left = `${screenX - screenWidth / 2}px`;
    this.htmlInputElement.style.top = `${screenY - screenHeight / 2}px`;
    this.htmlInputElement.style.width = `${screenWidth}px`;
    this.htmlInputElement.style.height = `${screenHeight}px`;
    this.htmlInputElement.style.fontSize = `${22 * Math.min(scaleX, scaleY)}px`;
    this.htmlInputElement.style.fontFamily = "Anton, sans-serif";
    this.htmlInputElement.style.backgroundColor = `#${hexToPhaser(COLORS.GRAY).toString(16).padStart(6, "0")}`;
    this.htmlInputElement.style.color = COLORS.WHITE;
    this.htmlInputElement.style.border = `3px solid ${COLORS.WHITE}`;
    this.htmlInputElement.style.padding = "12px";
    this.htmlInputElement.style.textAlign = "center";
    this.htmlInputElement.style.outline = "none";
    this.htmlInputElement.style.zIndex = "100002";
    this.htmlInputElement.style.boxSizing = "border-box";
    document.body.appendChild(this.htmlInputElement);

    const btnGap = 16;
    const btnY = panelY + panelHeight / 2 - 40;

    const buttonText = mode === "create-account" ? (i18next.t("ui:buttons.createAccount") || "CREATE ACCOUNT") : (i18next.t("ui:buttons.login") || "LOGIN");

    // Cancel button (left side)
    const cancelButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: i18next.t("ui:buttons.cancel") || "CANCEL",
      style: "danger",
      onClick: () => {
        this.hideEmailInputWindow();
      },
    });
    cancelButton.setScrollFactor(0);

    // Save button (right side) - initially disabled
    const saveButton = new ModernButton({
      scene: this.scene,
      x: 0, // Will be positioned after creation
      y: btnY,
      text: buttonText,
      style: "success",
      disabled: true,
      onClick: () => {
        const email = this.htmlInputElement?.value.trim() || "";
        if (email && this.isValidEmail(email)) {
          this.hideEmailInputWindow();
          // Show spinner and hide settings window for login mode
          if (mode === "login") {
            this.portalSpinner?.show();
            const callback = this.onCloseCallback;
            this.hide();
            callback?.();
          }
          this.sendEmailToServer(email, mode);
          trackEvent(
            mode === "create-account"
              ? "create-account-submitted"
              : "login-submitted",
          );
        }
      },
    });
    saveButton.setScrollFactor(0);

    // Position buttons
    const totalWidth =
      (cancelButton as any).buttonWidth +
      btnGap +
      (saveButton as any).buttonWidth;
    const cancelBtnX =
      panelX - totalWidth / 2 + (cancelButton as any).buttonWidth / 2;
    const saveBtnX =
      panelX + totalWidth / 2 - (saveButton as any).buttonWidth / 2;
    cancelButton.setX(cancelBtnX);
    saveButton.setX(saveBtnX);

    // Validation function
    const updateEmailValidation = () => {
      const email = this.htmlInputElement?.value || "";
      const isValid = this.isValidEmail(email.trim());

      // Update save button appearance
      saveButton.setDisabled(!isValid);
    };

    // Listen to input changes
    this.htmlInputElement.addEventListener("input", () => {
      updateEmailValidation();
    });

    // Handle Enter key to submit
    this.htmlInputElement.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const email = this.htmlInputElement?.value.trim() || "";
        if (email && this.isValidEmail(email)) {
          this.hideEmailInputWindow();
          // Show spinner and hide settings window for login mode
          if (mode === "login") {
            this.portalSpinner?.show();
            const callback = this.onCloseCallback;
            this.hide();
            callback?.();
          }
          this.sendEmailToServer(email, mode);
        }
      }
    });

    // Auto-focus the input
    setTimeout(() => {
      if (this.htmlInputElement) {
        this.htmlInputElement.focus();
      }
    }, 100);

    // Create container
    const containerElements: Phaser.GameObjects.GameObject[] = [
      bgOverlay,
      panelBg,
      title,
      cancelButton,
      saveButton,
    ];

    this.emailInputContainer = this.scene.add.container(
      0,
      0,
      containerElements,
    );
    this.emailInputContainer.setDepth(100001);
    this.emailInputContainer.setVisible(true);

    // Add email input container to UI layer
    this.uiLayer.add(this.emailInputContainer);
  }

  private hideEmailInputWindow() {
    // Remove HTML input element
    if (this.htmlInputElement) {
      this.htmlInputElement.remove();
      this.htmlInputElement = null;
    }

    // Destroy email input container
    if (this.emailInputContainer) {
      this.emailInputContainer.destroy();
      this.emailInputContainer = null;
    }

    // Restore player info and action slots visibility
    this.windowManager.showPlayerInfoWidget();
    const actionSlotsContainer = (this.windowManager as any).actionSlotsContainer;
    if (actionSlotsContainer) {
      actionSlotsContainer.setVisible(true);
    }

    // Restore UI buttons (inventory, player, shop icons)
    this.windowManager.showUIButtons(this.scene);
  }

  private sendEmailToServer(
    email: string,
    mode: "create-account" | "login" = "create-account",
  ) {
    const accountId = (window as any).accountId;
    let guestToken = localStorage.getItem("guestToken");

    if (!accountId) {
      console.error("No account ID available");
      this.notificationHandler.show(
        "✗ Error: Account ID not found. Please try reloading.",
        "error",
        5000,
      );
      return;
    }

    // For login mode, generate a new token to avoid conflicts with existing guest account
    if (mode === "login") {
      guestToken = generateUUID();
      localStorage.setItem("guestToken", guestToken);
      console.info(`[INFO] Generated new guest token for login: ${guestToken}`);
    }

    if (!guestToken) {
      console.error("No guest token available");
      this.notificationHandler.show(
        "✗ Error: Guest token not found. Please try reloading.",
        "error",
        5000,
      );
      return;
    }

    const socket = (window as any).socket;
    if (!socket) {
      console.error("No socket connection");
      this.notificationHandler.show(
        "✗ Error: Not connected to server.",
        "error",
        5000,
      );
      return;
    }

    const eventName =
      mode === "create-account" ? "create-account-email" : "login-email";

    socket.emit(
      eventName,
      { accountId, email, guestToken },
      (response: { success: boolean; message?: string }) => {
        // Always hide spinner when response is received
        if (this.portalSpinner) this.portalSpinner.hide();
        if (response && response.success) {
          const message =
            mode === "create-account"
              ? "✓ Account created! Check your inbox and click the link to verify your email."
              : "✓ Login email sent! Check your inbox and click the link to verify and login.";
          this.notificationHandler.show(message, "success", 5000);
          const callback = this.onCloseCallback;
          this.hide(); // Close settings window
          callback?.(); // Navigate back to raider-screen
        } else {
          console.error(`Failed to send ${mode} email:`, response.message);
          this.notificationHandler.show(
            "✗ " +
              (response.message ||
                "Failed to send verification email. Please try again later."),
            "error",
            5000,
          );
        }
      },
    );
  }
}
